/**
 * SAA reference — Admin management screen (#11).
 *
 * Two Read/Edit editors stacked on one page (5a Profiles, 5b Asset classes;
 * 5c Allocations follows). Each follows the app-wide Read/Edit-mode protocol
 * (§3g/§3h): Read mode is view-only; Edit enters a transaction where edits /
 * adds / removes / REORDERS are PENDING; "Save changes" commits the batch behind
 * the high-impact warning (firm-wide; cascades to the picker, the suitability
 * correspondence, and the ADQC/Annex II documents); Discard reverts; the shared
 * dirty-form guard gates navigating away. Direct write + audit (gated saa.manage).
 *
 * Ordering is by DRAG-AND-DROP (Pedro 2026-06-19): drag a row by its handle; the
 * Order number is the OUTCOME of the position (renumbered 1..N on drop), never
 * typed. Uses @hello-pangea/dnd (MIT, client-only UI lib — build-time, no runtime
 * egress; not a DORA Art. 28 entry, same class as @mantine/dropzone, C-196).
 */
import { useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Container, Group, Modal,
  NumberInput, SegmentedControl, Select, Stack, Table, Tabs, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArrowBackUp, IconChartLine, IconDownload, IconGripVertical,
  IconPencil, IconPlus, IconScale, IconTrash,
} from '@tabler/icons-react';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  saaApi, type SaaAssetClass, type SaaProfile, type SaaProfileMatrix,
  type SaaReturnsImportReport,
} from '../api/saa';
import { XlsxDropzone } from '../components/XlsxDropzone';
import { RequireAccess } from '../components/RequireAccess';
import { BackToCompliance } from '../components/BackToCompliance';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDirtyFormGuard } from '../hooks/useDirtyFormGuard';

const PROFILES_KEY = ['saa', 'profiles'] as const;
const CLASSES_KEY = ['saa', 'asset-classes'] as const;

const errMsg = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.');

const WARNING =
  'Changing the SAA definitions affects the SAA options advisers can pick on ' +
  'every Agreement, the suitability-score correspondence and ceiling checks, and ' +
  'the SAA tables shown in the ADQC questionnaire and generated Annex II ' +
  'documents. Existing agreements keep their recorded SAA; only new selections ' +
  'and newly-generated documents reflect this change.';

const CODE_RE = /^[A-Z0-9_]+$/;

function reorder<T>(arr: T[], from: number, to: number): T[] {
  const copy = arr.slice();
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved);
  return copy;
}

export function SaaAdminPage() {
  useDocumentTitle('SAA reference');
  return (
    <RequireAccess permission="saa.manage">
      <Container size="xl">
        <Stack gap="lg">
          <BackToCompliance to="/admin" label="Back to Admin" />
          <Tabs defaultValue="profiles" keepMounted>
            <Tabs.List>
              <Tabs.Tab value="profiles">Profiles</Tabs.Tab>
              <Tabs.Tab value="classes">Asset classes</Tabs.Tab>
              <Tabs.Tab value="allocations">Allocations</Tabs.Tab>
              <Tabs.Tab value="returns">Daily returns</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="profiles" pt="lg"><ProfilesEditor /></Tabs.Panel>
            <Tabs.Panel value="classes" pt="lg"><AssetClassesEditor /></Tabs.Panel>
            <Tabs.Panel value="allocations" pt="lg"><AllocationsEditor /></Tabs.Panel>
            <Tabs.Panel value="returns" pt="lg"><DailyReturnsSection /></Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    </RequireAccess>
  );
}

function SaveWarningModal({ opened, onCancel, onConfirm, saving }: {
  opened: boolean; onCancel: () => void; onConfirm: () => void; saving: boolean;
}) {
  return (
    <Modal opened={opened} onClose={onCancel} centered title={<Text fw={600}>Save SAA changes</Text>}>
      <Stack>
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="High-impact change">{WARNING}</Alert>
        <Text size="sm">Apply your changes? Removals are blocked if the item is in use.</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>Cancel</Button>
          <Button color="azul" loading={saving} onClick={onConfirm}>Save changes</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function EditHeader({ title, editMode, onEdit, onDiscard, onAdd, addLabel, onSave, saving }: {
  title: string; editMode: boolean; onEdit: () => void; onDiscard: () => void;
  onAdd: () => void; addLabel: string; onSave: () => void; saving: boolean;
}) {
  return (
    <Group justify="space-between">
      <Group gap="xs"><IconScale size={20} /><Title order={3}>{title}</Title></Group>
      {!editMode ? (
        <Button leftSection={<IconPencil size={16} />} onClick={onEdit}>Edit</Button>
      ) : (
        <Group gap="xs">
          <Button variant="default" leftSection={<IconArrowBackUp size={16} />} onClick={onDiscard}>Discard</Button>
          <Button variant="light" leftSection={<IconPlus size={16} />} onClick={onAdd}>{addLabel}</Button>
          <Button color="azul" loading={saving} onClick={onSave}>Save changes</Button>
        </Group>
      )}
    </Group>
  );
}

// ── Profiles editor (5a) ─────────────────────────────────────────────────────
interface ProfileDraft {
  rowKey: string; code: string; label: string; sort_order: number;
  origin: 'existing' | 'new'; deleted: boolean; origLabel: string; origSort: number;
}
const profilesToDraft = (ps: SaaProfile[]): ProfileDraft[] => ps.map((p) => ({
  rowKey: p.code, code: p.code, label: p.label, sort_order: p.sort_order,
  origin: 'existing', deleted: false, origLabel: p.label, origSort: p.sort_order,
}));

function ProfilesEditor() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: PROFILES_KEY, queryFn: saaApi.profiles });

  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<ProfileDraft[]>([]);
  const [seq, setSeq] = useState(0);
  const [saving, setSaving] = useState(false);
  const [warn, setWarn] = useState(false);

  const dirty = useMemo(() => editMode && draft.some((r) =>
    (r.origin === 'new' && !r.deleted) || (r.origin === 'existing' && r.deleted) ||
    (r.origin === 'existing' && (r.label !== r.origLabel || r.sort_order !== r.origSort))), [editMode, draft]);
  const confirmDiscard = useDirtyFormGuard(dirty);

  const enter = () => { setDraft(profilesToDraft(q.data ?? [])); setEditMode(true); };
  const leave = () => { setEditMode(false); setDraft([]); };
  const patch = (k: string, p: Partial<ProfileDraft>) => setDraft((d) => d.map((r) => (r.rowKey === k ? { ...r, ...p } : r)));
  const addRow = () => {
    const next = draft.length + 1;
    setSeq((n) => n + 1);
    setDraft((d) => [...d, { rowKey: `new-${seq}`, code: '', label: '', sort_order: next, origin: 'new', deleted: false, origLabel: '', origSort: next }]);
  };
  const removeRow = (r: ProfileDraft) => r.origin === 'new' ? setDraft((d) => d.filter((x) => x.rowKey !== r.rowKey)) : patch(r.rowKey, { deleted: true });
  const onDragEnd = (res: DropResult) => {
    if (!res.destination) return;
    setDraft((d) => reorder(d, res.source.index, res.destination!.index).map((r, i) => ({ ...r, sort_order: i + 1 })));
  };

  function validate(): string | null {
    const live = draft.filter((r) => !r.deleted);
    for (const r of live) {
      if (r.origin === 'new' && !CODE_RE.test(r.code)) return `New code "${r.code || '(empty)'}" must be UPPERCASE / digits / underscore.`;
      if (r.label.trim().length === 0) return `Profile "${r.code}" needs a label.`;
    }
    const codes = live.map((r) => r.code);
    const dc = codes.find((c, i) => codes.indexOf(c) !== i);
    if (dc) return `Duplicate profile code "${dc}".`;
    return null;
  }
  async function save() {
    setWarn(false); setSaving(true);
    const fail: string[] = [];
    try {
      for (const r of draft.filter((x) => x.origin === 'existing' && x.deleted))
        try { await saaApi.deleteProfile(r.code); } catch (e) { fail.push(`Remove ${r.code}: ${(e as ApiError).detail}`); }
      for (const r of draft.filter((x) => x.origin === 'new' && !x.deleted))
        try { await saaApi.createProfile({ code: r.code, label: r.label.trim(), sort_order: r.sort_order }); } catch (e) { fail.push(`Add ${r.code}: ${(e as ApiError).detail}`); }
      for (const r of draft.filter((x) => x.origin === 'existing' && !x.deleted && (x.label !== x.origLabel || x.sort_order !== x.origSort)))
        try { await saaApi.updateProfile(r.code, { label: r.label.trim(), sort_order: r.sort_order }); } catch (e) { fail.push(`Update ${r.code}: ${(e as ApiError).detail}`); }
    } finally { setSaving(false); }
    await qc.invalidateQueries({ queryKey: PROFILES_KEY });
    if (fail.length === 0) { notifications.show({ color: 'green', message: 'SAA profiles saved' }); leave(); }
    else { notifications.show({ color: 'red', title: 'Some changes were not saved', autoClose: false, message: fail.join(' · ') }); setDraft(profilesToDraft(qc.getQueryData<SaaProfile[]>(PROFILES_KEY) ?? [])); }
  }
  function onSave() {
    const e = validate();
    if (e) { notifications.show({ color: 'red', title: 'Cannot save', message: e }); return; }
    if (!dirty) { leave(); return; }
    setWarn(true);
  }

  const rows = editMode ? draft : profilesToDraft(q.data ?? []);
  return (
    <Stack>
      <EditHeader title="Profiles" editMode={editMode} onEdit={enter} onDiscard={() => confirmDiscard(leave)} onAdd={addRow} addLabel="Add profile" onSave={onSave} saving={saving} />
      <Text c="dimmed" size="sm">Strategic Asset Allocation profiles (MM, MM+, STB, SAA00–SAA100). Admin-managed; firm-wide. Drag the handle to reorder.</Text>
      {q.isError && <Text c="red">Failed to load profiles.</Text>}
      <DragDropContext onDragEnd={onDragEnd}>
        <Table withTableBorder>
          <Table.Thead><Table.Tr>
            {editMode && <Table.Th w={40} />}
            <Table.Th w={160}>Code</Table.Th><Table.Th>Label</Table.Th><Table.Th w={80}>Order</Table.Th>{editMode && <Table.Th w={70} />}
          </Table.Tr></Table.Thead>
          <Droppable droppableId="profiles">
            {(dp) => (
              <Table.Tbody ref={dp.innerRef} {...dp.droppableProps}>
                {rows.map((r, i) => (
                  <Draggable key={r.rowKey} draggableId={r.rowKey} index={i} isDragDisabled={!editMode || r.deleted}>
                    {(dr) => (
                      <Table.Tr ref={dr.innerRef} {...dr.draggableProps} style={{ ...dr.draggableProps.style, ...(r.deleted ? { opacity: 0.4 } : {}) }}>
                        {editMode && <Table.Td {...dr.dragHandleProps} style={{ cursor: r.deleted ? 'default' : 'grab' }}>{!r.deleted && <IconGripVertical size={16} color="var(--mantine-color-gray-5)" />}</Table.Td>}
                        <Table.Td>{editMode && r.origin === 'new'
                          ? <TextInput size="xs" placeholder="CODE" value={r.code} onChange={(e) => patch(r.rowKey, { code: e.currentTarget.value.toUpperCase() })} />
                          : <Text ff="monospace">{r.code}</Text>}</Table.Td>
                        <Table.Td>{editMode && !r.deleted
                          ? <TextInput size="xs" value={r.label} onChange={(e) => patch(r.rowKey, { label: e.currentTarget.value })} />
                          : <Text td={r.deleted ? 'line-through' : undefined}>{r.label}</Text>}</Table.Td>
                        <Table.Td><Text>{r.sort_order}</Text></Table.Td>
                        {editMode && <Table.Td>{r.deleted
                          ? <Tooltip label="Undo remove" withArrow><ActionIcon variant="subtle" onClick={() => patch(r.rowKey, { deleted: false })} aria-label="Undo"><IconArrowBackUp size={16} /></ActionIcon></Tooltip>
                          : <Tooltip label="Remove" withArrow><ActionIcon variant="subtle" color="red" onClick={() => removeRow(r)} aria-label="Remove"><IconTrash size={16} /></ActionIcon></Tooltip>}</Table.Td>}
                      </Table.Tr>
                    )}
                  </Draggable>
                ))}
                {dp.placeholder}
              </Table.Tbody>
            )}
          </Droppable>
        </Table>
      </DragDropContext>
      <SaveWarningModal opened={warn} saving={saving} onCancel={() => setWarn(false)} onConfirm={save} />
    </Stack>
  );
}

// ── Asset-class taxonomy editor (5b) ─────────────────────────────────────────
interface AcDraft {
  rowKey: string; code: string; label: string; parent_code: string | null;
  benchmark: string; sort_order: number;
  origin: 'existing' | 'new'; deleted: boolean;
  origLabel: string; origParent: string | null; origBenchmark: string; origSort: number;
}
const classesToDraft = (cs: SaaAssetClass[]): AcDraft[] => cs.map((c) => ({
  rowKey: c.code, code: c.code, label: c.label, parent_code: c.parent_code,
  benchmark: c.benchmark_ticker ?? '', sort_order: c.sort_order,
  origin: 'existing', deleted: false,
  origLabel: c.label, origParent: c.parent_code, origBenchmark: c.benchmark_ticker ?? '', origSort: c.sort_order,
}));

function AssetClassesEditor() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: CLASSES_KEY, queryFn: saaApi.assetClasses });

  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<AcDraft[]>([]);
  const [seq, setSeq] = useState(0);
  const [saving, setSaving] = useState(false);
  const [warn, setWarn] = useState(false);

  const dirty = useMemo(() => editMode && draft.some((r) =>
    (r.origin === 'new' && !r.deleted) || (r.origin === 'existing' && r.deleted) ||
    (r.origin === 'existing' && (r.label !== r.origLabel || r.parent_code !== r.origParent
      || r.benchmark !== r.origBenchmark || r.sort_order !== r.origSort))), [editMode, draft]);
  const confirmDiscard = useDirtyFormGuard(dirty);

  const enter = () => { setDraft(classesToDraft(q.data ?? [])); setEditMode(true); };
  const leave = () => { setEditMode(false); setDraft([]); };
  const patch = (k: string, p: Partial<AcDraft>) => setDraft((d) => d.map((r) => (r.rowKey === k ? { ...r, ...p } : r)));
  const addRow = () => {
    const next = draft.length + 1;
    setSeq((n) => n + 1);
    setDraft((d) => [...d, { rowKey: `new-${seq}`, code: '', label: '', parent_code: null, benchmark: '', sort_order: next, origin: 'new', deleted: false, origLabel: '', origParent: null, origBenchmark: '', origSort: next }]);
  };
  const removeRow = (r: AcDraft) => r.origin === 'new' ? setDraft((d) => d.filter((x) => x.rowKey !== r.rowKey)) : patch(r.rowKey, { deleted: true });
  const onDragEnd = (res: DropResult) => {
    if (!res.destination) return;
    setDraft((d) => reorder(d, res.source.index, res.destination!.index).map((r, i) => ({ ...r, sort_order: i + 1 })));
  };

  const parentOptions = useMemo(() => draft
    .filter((r) => !r.deleted && r.parent_code === null && r.code.trim() !== '')
    .map((r) => ({ value: r.code, label: `${r.label || r.code} (${r.code})` })), [draft]);

  function validate(): string | null {
    const live = draft.filter((r) => !r.deleted);
    const topCodes = new Set(live.filter((r) => r.parent_code === null).map((r) => r.code));
    for (const r of live) {
      if (r.origin === 'new' && !CODE_RE.test(r.code)) return `New code "${r.code || '(empty)'}" must be UPPERCASE / digits / underscore.`;
      if (r.label.trim().length === 0) return `Asset class "${r.code}" needs a label.`;
      if (r.parent_code !== null) {
        if (r.parent_code === r.code) return `"${r.code}" cannot be its own parent.`;
        if (!topCodes.has(r.parent_code)) return `Parent of "${r.code}" must be a top-level class (one level of nesting).`;
      }
    }
    const codes = live.map((r) => r.code);
    const dc = codes.find((c, i) => codes.indexOf(c) !== i);
    if (dc) return `Duplicate asset-class code "${dc}".`;
    return null;
  }
  async function save() {
    setWarn(false); setSaving(true);
    const fail: string[] = [];
    try {
      for (const r of draft.filter((x) => x.origin === 'existing' && x.deleted))
        try { await saaApi.deleteAssetClass(r.code); } catch (e) { fail.push(`Remove ${r.code}: ${(e as ApiError).detail}`); }
      const news = draft.filter((x) => x.origin === 'new' && !x.deleted)
        .sort((a, b) => Number(a.parent_code !== null) - Number(b.parent_code !== null));
      for (const r of news)
        try { await saaApi.createAssetClass({ code: r.code, label: r.label.trim(), parent_code: r.parent_code, benchmark_ticker: r.benchmark.trim() || null, sort_order: r.sort_order }); }
        catch (e) { fail.push(`Add ${r.code}: ${(e as ApiError).detail}`); }
      for (const r of draft.filter((x) => x.origin === 'existing' && !x.deleted
        && (x.label !== x.origLabel || x.parent_code !== x.origParent || x.benchmark !== x.origBenchmark || x.sort_order !== x.origSort)))
        try { await saaApi.updateAssetClass(r.code, { label: r.label.trim(), parent_code: r.parent_code ?? '', benchmark_ticker: r.benchmark.trim(), sort_order: r.sort_order }); }
        catch (e) { fail.push(`Update ${r.code}: ${(e as ApiError).detail}`); }
    } finally { setSaving(false); }
    await qc.invalidateQueries({ queryKey: CLASSES_KEY });
    if (fail.length === 0) { notifications.show({ color: 'green', message: 'Asset classes saved' }); leave(); }
    else { notifications.show({ color: 'red', title: 'Some changes were not saved', autoClose: false, message: fail.join(' · ') }); setDraft(classesToDraft(qc.getQueryData<SaaAssetClass[]>(CLASSES_KEY) ?? [])); }
  }
  function onSave() {
    const e = validate();
    if (e) { notifications.show({ color: 'red', title: 'Cannot save', message: e }); return; }
    if (!dirty) { leave(); return; }
    setWarn(true);
  }

  const rows = editMode ? draft : classesToDraft(q.data ?? []);
  return (
    <Stack>
      <EditHeader title="Asset classes" editMode={editMode} onEdit={enter} onDiscard={() => confirmDiscard(leave)} onAdd={addRow} addLabel="Add class" onSave={onSave} saving={saving} />
      <Text c="dimmed" size="sm">The shared asset-class taxonomy (one level of nesting) + each class's benchmark. Used by every profile's allocation matrix. Drag the handle to reorder.</Text>
      {q.isError && <Text c="red">Failed to load asset classes.</Text>}
      <DragDropContext onDragEnd={onDragEnd}>
        <Table withTableBorder>
          <Table.Thead><Table.Tr>
            {editMode && <Table.Th w={40} />}
            <Table.Th w={170}>Code</Table.Th><Table.Th>Label</Table.Th><Table.Th w={220}>Parent</Table.Th>
            <Table.Th w={180}>Benchmark</Table.Th><Table.Th w={80}>Order</Table.Th>{editMode && <Table.Th w={70} />}
          </Table.Tr></Table.Thead>
          <Droppable droppableId="asset-classes">
            {(dp) => (
              <Table.Tbody ref={dp.innerRef} {...dp.droppableProps}>
                {rows.map((r, i) => (
                  <Draggable key={r.rowKey} draggableId={r.rowKey} index={i} isDragDisabled={!editMode || r.deleted}>
                    {(dr) => (
                      <Table.Tr ref={dr.innerRef} {...dr.draggableProps} style={{ ...dr.draggableProps.style, ...(r.deleted ? { opacity: 0.4 } : {}) }}>
                        {editMode && <Table.Td {...dr.dragHandleProps} style={{ cursor: r.deleted ? 'default' : 'grab' }}>{!r.deleted && <IconGripVertical size={16} color="var(--mantine-color-gray-5)" />}</Table.Td>}
                        <Table.Td>{editMode && r.origin === 'new'
                          ? <TextInput size="xs" placeholder="CODE" value={r.code} onChange={(e) => patch(r.rowKey, { code: e.currentTarget.value.toUpperCase() })} />
                          : <Text ff="monospace" pl={r.parent_code ? 'md' : 0}>{r.code}</Text>}</Table.Td>
                        <Table.Td>{editMode && !r.deleted
                          ? <TextInput size="xs" value={r.label} onChange={(e) => patch(r.rowKey, { label: e.currentTarget.value })} />
                          : <Text td={r.deleted ? 'line-through' : undefined} pl={r.parent_code ? 'md' : 0}>{r.label}</Text>}</Table.Td>
                        <Table.Td>{editMode && !r.deleted
                          ? <Select size="xs" clearable placeholder="(top-level)" data={parentOptions.filter((o) => o.value !== r.code)} value={r.parent_code} onChange={(v) => patch(r.rowKey, { parent_code: v })} />
                          : <Text c="dimmed" size="sm">{r.parent_code ?? '—'}</Text>}</Table.Td>
                        <Table.Td>{editMode && !r.deleted
                          ? <TextInput size="xs" placeholder="(none)" value={r.benchmark} onChange={(e) => patch(r.rowKey, { benchmark: e.currentTarget.value })} />
                          : <Text size="sm">{r.benchmark || '—'}</Text>}</Table.Td>
                        <Table.Td><Text>{r.sort_order}</Text></Table.Td>
                        {editMode && <Table.Td>{r.deleted
                          ? <Tooltip label="Undo remove" withArrow><ActionIcon variant="subtle" onClick={() => patch(r.rowKey, { deleted: false })} aria-label="Undo"><IconArrowBackUp size={16} /></ActionIcon></Tooltip>
                          : <Tooltip label="Remove" withArrow><ActionIcon variant="subtle" color="red" onClick={() => removeRow(r)} aria-label="Remove"><IconTrash size={16} /></ActionIcon></Tooltip>}</Table.Td>}
                      </Table.Tr>
                    )}
                  </Draggable>
                ))}
                {dp.placeholder}
              </Table.Tbody>
            )}
          </Droppable>
        </Table>
      </DragDropContext>
      <SaveWarningModal opened={warn} saving={saving} onCancel={() => setWarn(false)} onConfirm={save} />
    </Stack>
  );
}

// ── Allocation matrix editor (5c) — per profile ──────────────────────────────
interface CellDraft {
  asset_code: string; asset_label: string; parent_code: string | null; benchmark: string | null;
  min: number; neutral: number | null; max: number;
  origMin: number; origNeutral: number | null; origMax: number;
}
const matrixToDraft = (m: SaaProfileMatrix | undefined): CellDraft[] => (m?.cells ?? []).map((c) => ({
  asset_code: c.asset_code, asset_label: c.asset_label, parent_code: c.parent_code, benchmark: c.benchmark_ticker,
  min: c.min_pct, neutral: c.neutral_pct, max: c.max_pct,
  origMin: c.min_pct, origNeutral: c.neutral_pct, origMax: c.max_pct,
}));

function AllocationsEditor() {
  const qc = useQueryClient();
  const profilesQ = useQuery({ queryKey: PROFILES_KEY, queryFn: saaApi.profiles });
  const [picked, setPicked] = useState<string | null>(null);
  const code = picked ?? profilesQ.data?.[0]?.code ?? null;

  const matrixQ = useQuery({
    queryKey: ['saa', 'alloc', code],
    queryFn: () => saaApi.allocations(code as string),
    enabled: !!code,
  });

  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<CellDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [warn, setWarn] = useState(false);

  const dirty = useMemo(() => editMode && draft.some((r) =>
    r.min !== r.origMin || r.neutral !== r.origNeutral || r.max !== r.origMax), [editMode, draft]);
  const confirmDiscard = useDirtyFormGuard(dirty);

  const enter = () => { setDraft(matrixToDraft(matrixQ.data)); setEditMode(true); };
  const leave = () => { setEditMode(false); setDraft([]); };
  const patch = (asset: string, p: Partial<CellDraft>) =>
    setDraft((d) => d.map((r) => (r.asset_code === asset ? { ...r, ...p } : r)));

  function validate(): string | null {
    for (const r of draft) {
      if (r.min < 0 || r.min > 100 || r.max < 0 || r.max > 100) return `${r.asset_label}: min/max must be 0–100.`;
      if (r.min > r.max) return `${r.asset_label}: min must be ≤ max.`;
      if (r.neutral !== null && (r.neutral < 0 || r.neutral > 100)) return `${r.asset_label}: neutral must be 0–100.`;
      if (r.neutral !== null && (r.neutral < r.min || r.neutral > r.max)) return `${r.asset_label}: neutral must be within min–max.`;
    }
    return null;
  }
  async function save() {
    setWarn(false); setSaving(true);
    const fail: string[] = [];
    try {
      for (const r of draft.filter((x) => x.min !== x.origMin || x.neutral !== x.origNeutral || x.max !== x.origMax))
        try { await saaApi.upsertAllocation(code as string, r.asset_code, { min_pct: r.min, neutral_pct: r.neutral, max_pct: r.max }); }
        catch (e) { fail.push(`${r.asset_code}: ${(e as ApiError).detail}`); }
    } finally { setSaving(false); }
    await qc.invalidateQueries({ queryKey: ['saa', 'alloc', code] });
    if (fail.length === 0) { notifications.show({ color: 'green', message: 'Allocations saved' }); leave(); }
    else { notifications.show({ color: 'red', title: 'Some changes were not saved', autoClose: false, message: fail.join(' · ') }); setDraft(matrixToDraft(qc.getQueryData<SaaProfileMatrix>(['saa', 'alloc', code]))); }
  }
  function onSave() {
    const e = validate();
    if (e) { notifications.show({ color: 'red', title: 'Cannot save', message: e }); return; }
    if (!dirty) { leave(); return; }
    setWarn(true);
  }

  const rows = editMode ? draft : matrixToDraft(matrixQ.data);
  const profileOpts = (profilesQ.data ?? []).map((p) => ({ value: p.code, label: p.label }));

  return (
    <Stack>
      <Group justify="space-between">
        <Group gap="xs"><IconScale size={20} /><Title order={3}>Allocations</Title></Group>
        {!editMode ? (
          <Button leftSection={<IconPencil size={16} />} onClick={enter} disabled={!code}>Edit</Button>
        ) : (
          <Group gap="xs">
            <Button variant="default" leftSection={<IconArrowBackUp size={16} />} onClick={() => confirmDiscard(leave)}>Discard</Button>
            <Button color="azul" loading={saving} onClick={onSave}>Save changes</Button>
          </Group>
        )}
      </Group>
      <Group align="flex-end">
        <Select label="Profile" data={profileOpts} value={code} onChange={(v) => confirmDiscard(() => { leave(); setPicked(v); })}
          disabled={editMode} w={260} allowDeselect={false} />
        <Text c="dimmed" size="sm" pb={6}>Min / Neutral / Max %. Blank Neutral = “may hold occasionally”.</Text>
      </Group>
      {matrixQ.isError && <Text c="red">Failed to load allocations.</Text>}
      <Table withTableBorder>
        <Table.Thead><Table.Tr>
          <Table.Th>Asset class</Table.Th><Table.Th w={170}>Benchmark</Table.Th>
          <Table.Th w={110}>Min %</Table.Th><Table.Th w={120}>Neutral %</Table.Th><Table.Th w={110}>Max %</Table.Th>
        </Table.Tr></Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <Table.Tr key={r.asset_code}>
              <Table.Td><Text pl={r.parent_code ? 'md' : 0} fw={r.parent_code ? 400 : 600}>{r.asset_label}</Text></Table.Td>
              <Table.Td><Text size="sm" c="dimmed">{r.benchmark || '—'}</Text></Table.Td>
              <Table.Td>{editMode
                ? <NumberInput size="xs" min={0} max={100} value={r.min} onChange={(v) => patch(r.asset_code, { min: typeof v === 'number' ? v : 0 })} />
                : <Text>{r.min}</Text>}</Table.Td>
              <Table.Td>{editMode
                ? <NumberInput size="xs" min={0} max={100} placeholder="—" value={r.neutral ?? ''} onChange={(v) => patch(r.asset_code, { neutral: v === '' || v === undefined ? null : Number(v) })} />
                : <Text>{r.neutral ?? '—'}</Text>}</Table.Td>
              <Table.Td>{editMode
                ? <NumberInput size="xs" min={0} max={100} value={r.max} onChange={(v) => patch(r.asset_code, { max: typeof v === 'number' ? v : 0 })} />
                : <Text>{r.max}</Text>}</Table.Td>
            </Table.Tr>
          ))}
          {!matrixQ.isLoading && rows.length === 0 && (
            <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">{code ? 'No allocation cells.' : 'Select a profile.'}</Text></Table.Td></Table.Tr>
          )}
        </Table.Tbody>
      </Table>
      <SaveWarningModal opened={warn} saving={saving} onCancel={() => setWarn(false)} onConfirm={save} />
    </Stack>
  );
}

// ── Daily returns (Item C inc 1c) ────────────────────────────────────────────
function DailyReturnsSection() {
  return (
    <Stack gap="md">
      <Group gap="xs"><IconChartLine size={20} /><Title order={3}>Daily returns</Title></Group>
      <Text size="sm" c="dimmed">
        The firm-wide SAA daily-return series — the single source of SAA performance, reused
        by portfolio returns, the Monthly Report, MIS and planning. Upload a Date × profiles
        matrix of decimal daily returns. Querying and analysing the series has moved to the
        all-roles <b>SAA</b> card on the Welcome page.
      </Text>
      <ReturnsUpload />
    </Stack>
  );
}

function ReturnsUpload() {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<SaaReturnsImportReport | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [onConflict, setOnConflict] = useState<'skip' | 'overwrite'>('overwrite');

  async function handleDownload() {
    setDownloading(true);
    try { await saaApi.returnsDownloadTemplate(); }
    catch (err) { notifications.show({ color: 'red', title: 'Template download failed', message: errMsg(err) }); }
    finally { setDownloading(false); }
  }
  async function handleSelect(f: File) {
    setFile(f); setPreview(null); setPreviewing(true);
    try { setPreview(await saaApi.returnsImport(f, 'preview', onConflict)); }
    catch (err) { notifications.show({ color: 'red', title: 'Preview failed', message: errMsg(err) }); setFile(null); }
    finally { setPreviewing(false); }
  }
  async function handleCommit() {
    if (!file) return;
    setCommitting(true);
    try {
      const r = await saaApi.returnsImport(file, 'commit', onConflict);
      notifications.show({
        color: 'green', title: 'SAA returns imported',
        message: `${r.inserted} inserted · ${r.overwritten} overwritten · ${r.skipped} skipped.`,
      });
      setFile(null); setPreview(null);
      qc.invalidateQueries({ queryKey: ['saa', 'returns'] });
    } catch (err) {
      notifications.show({ color: 'red', title: 'Import failed', message: errMsg(err) });
    } finally { setCommitting(false); }
  }

  return (
    <Card withBorder padding="lg">
      <Stack>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <Title order={4}>Upload daily returns</Title>
            <Text c="dimmed" size="sm">
              A <b>Date</b> column + one column per SAA profile (by label), dates in rows,
              decimal daily returns (0.0012 = +0.12%). Paste your Bloomberg block under the
              template header, then upload.
            </Text>
          </div>
          <Button variant="light" leftSection={<IconDownload size={16} />} onClick={handleDownload}
            loading={downloading} style={{ flexShrink: 0 }}>Download template</Button>
        </Group>

        {!preview ? (
          <XlsxDropzone
            hint="One Excel file: the daily-returns template, with the returns pasted under its header."
            onSelect={handleSelect}
            loading={previewing}
            onReject={() => notifications.show({ color: 'red', title: 'Not accepted', message: 'Please choose a single .xlsx workbook.' })}
          />
        ) : (
          <Stack>
            <ReturnsReview report={preview} fileName={file?.name ?? ''} />
            <Group justify="space-between">
              <SegmentedControl
                value={onConflict}
                onChange={(v) => setOnConflict(v as 'skip' | 'overwrite')}
                data={[{ label: 'Overwrite existing', value: 'overwrite' }, { label: 'Skip existing', value: 'skip' }]}
              />
              <Group>
                <Button variant="default" onClick={() => { setFile(null); setPreview(null); }}>Cancel</Button>
                <Button color="azul" loading={committing} disabled={preview.total_cells === 0} onClick={handleCommit}>
                  Import {preview.total_cells} cell{preview.total_cells === 1 ? '' : 's'}
                </Button>
              </Group>
            </Group>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function ReturnsReview({ report, fileName }: { report: SaaReturnsImportReport; fileName: string }) {
  return (
    <Stack gap="xs">
      <Text size="sm">
        <b>{fileName}</b> — {report.total_cells} return cell{report.total_cells === 1 ? '' : 's'} across{' '}
        {report.n_dates} date{report.n_dates === 1 ? '' : 's'} ({report.date_from ?? '—'} → {report.date_to ?? '—'}),{' '}
        {report.profiles_matched} profile{report.profiles_matched === 1 ? '' : 's'}.
      </Text>
      <Group gap="xs">
        <Badge color="green" variant="light">{report.new_count} new</Badge>
        <Badge color="orange" variant="light">{report.conflict_count} already stored</Badge>
      </Group>
      {report.profile_labels.length > 0 && (
        <Text size="xs" c="dimmed">Profiles: {report.profile_labels.join(' · ')}</Text>
      )}
      {report.unknown_columns.length > 0 && (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Unrecognised columns (ignored)">
          {report.unknown_columns.join(', ')} — matched no SAA profile (by code or label).
        </Alert>
      )}
      {report.errors.length > 0 && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title={`${report.errors.length} cell/row issue(s)`}>
          <Stack gap={2}>{report.errors.slice(0, 8).map((e, i) => <Text size="xs" key={i}>{e}</Text>)}</Stack>
        </Alert>
      )}
    </Stack>
  );
}

